import React, { useMemo } from 'react';
import { VictoryContainer, VictoryPie } from 'victory';
import { useTranslation } from 'react-i18next';
import styles from "./styles.module.scss";
import { USGAAdvancedStats } from './types';

type GirAccuracyProps = {
  data: USGAAdvancedStats;
};

type ChartDatum = {
  length: number;
  y: number;
  color: string;
  index: number;
};

const CustomLabel = ({ label, value }: { label: string; value: string }) => (
  <div className={styles.label_container}>
    <div className={styles.percent}>{value}</div>
    <div className={styles.text}>{label}</div>
  </div>
);

const interpolate = (
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
) => outMin + ((value - inMin) * (outMax - outMin)) / (inMax - inMin);

export default function GirAccuracy({ data }: GirAccuracyProps) {
  const { t } = useTranslation();

  const chartData: ChartDatum[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const keys = [
      'missedLongApproachShotAccuracyPercent',
      'missedRightApproachShotAccuracyPercent',
      'missedShortApproachShotAccuracyPercent',
      'missedLeftApproachShotAccuracyPercent',
    ] as const;

    const maxKey = keys.reduce((a, b) =>
      (data[a] ?? 0) > (data[b] ?? 0) ? a : b
    );

    return [
      {
        length: data.missedLongApproachShotAccuracyPercent / 100,
        y: 25,
        color: maxKey === keys[0] ? '#E54500' : '#CECECE',
        index: 4,
      },
      {
        length: data.missedRightApproachShotAccuracyPercent / 100,
        y: 25,
        color: maxKey === keys[1] ? '#E54500' : '#CECECE',
        index: 3,
      },
      {
        length: data.missedShortApproachShotAccuracyPercent / 100,
        y: 25,
        color: maxKey === keys[2] ? '#E54500' : '#CECECE',
        index: 2,
      },
      {
        length: data.missedLeftApproachShotAccuracyPercent / 100,
        y: 25,
        color: maxKey === keys[3] ? '#E54500' : '#CECECE',
        index: 1,
      },
    ];
  }, [data]);

  return (
    <div className={styles.gir_container}>
      <div className={styles.pie_container}>
        <VictoryPie
          padding={0}
          width={220}
          height={220}
          innerRadius={64}
          startAngle={-45}
          data={[{ y: 25 }, { y: 25 }, { y: 25 }, { y: 25 }]}
          style={{
            data: { fill: '#F4F4F4' },
            labels: { display: 'none' },
          }}
          animate={false}
          padAngle={2}
          containerComponent={<VictoryContainer responsive={false} />}
        />
        <div className={styles.absolute_position_pie}>
          <VictoryPie
            padding={0}
            width={220}
            height={220}
            innerRadius={64}
            startAngle={-45}
            data={chartData}
            radius={({ datum }) =>
              interpolate(Math.sqrt(datum.length * 100) / 10, 0, 1, 64, 110)
            }
            style={{
              data: { fill: ({ datum }) => datum.color },
              labels: { display: 'none' },
            }}
            animate={false}
            padAngle={2}
            containerComponent={<VictoryContainer responsive={false} />}
          />
        </div>
        <div className={styles.inner_container}>
          <VictoryPie
            padding={0}
            width={105}
            height={105}
            innerRadius={37}
            data={[
              { y: data?.girPercent, color: '#43a047' },
              { y: 100 - data?.girPercent, color: '#F4F4F4' },
            ]}
            style={{
              data: { fill: ({ datum }) => datum.color },
              labels: { display: 'none' },
            }}
            containerComponent={<VictoryContainer responsive={false} />}
          />
          <div className={styles.inner_text_container}>
            <div className={styles.percent}>{data?.girPercent}%</div>
            <div className={styles.label}>{t('GIR')}</div>
          </div>
        </div>
      </div>

      <div className={styles.vertical_labels}>
        <CustomLabel
          label={t('LONG')}
          value={`${data?.missedLongApproachShotAccuracyPercent}%`}
        />
        <CustomLabel
          label={t('SHORT')}
          value={`${data?.missedShortApproachShotAccuracyPercent}%`}
        />
      </div>
      <div className={styles.horizontal_labels}>
        <CustomLabel
          label={t('LEFT')}
          value={`${data?.missedLeftApproachShotAccuracyPercent}%`}
        />
        <CustomLabel
          label={t('RIGHT')}
          value={`${data?.missedRightApproachShotAccuracyPercent}%`}
        />
      </div>
    </div>
  );
}
